import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { formatPrice, joinClassNames } from "@utils/helpers";
import { useWindowSize } from "@utils/hooks";
import { Tooltip, Text, Heading, Card, Button } from "@components/theming";
import { useIsShopsite } from "@context/DomainContext";
import DialogCloseButton from "@components/popovers/dialogCloseButton";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";

interface Props {
  fees: {
    id: number | string;
    name: string;
    description: string;
    price: string | number;
  }[];
  title: string;
  description: string;
  currency: string;
}

const Feetooltip = ({ fees, title, description, currency }: Props) => {
  const windowSize = useWindowSize();
  const [open, setOpen] = useState(false);
  const isShopsite = useIsShopsite();
  const t = useTranslations("Checkout.Cart.tickets.fees");
  const locale = useLocale();

  return windowSize.width && windowSize.width > 768 ? (
    <Tooltip
      sideOffset={10}
      content={
        <span>
          <span className="flex flex-col">
            <Text as="span" sharedClasses="text-xl font-bold">
              {title}
            </Text>
            <Text as="span" sharedClasses="text-sm">
              {description}
            </Text>
          </span>
          {fees.map((fee) => (
            <span
              key={fee.id}
              className="flex w-full items-center gap-6 mt-2 justify-between"
            >
              <span className="min-w-0 flex flex-col flex-auto">
                <Text as="span" size="3">
                  {fee.name}
                </Text>
                <Text as="span" className=" text-sm leading-5">
                  {fee.description}
                </Text>
              </span>
              <span
                className={joinClassNames(
                  "font-medium tabular-nums",
                  typeof fee.price === "string"
                    ? fee.price.includes("$")
                      ? "mr-1"
                      : ""
                    : ""
                )}
              >
                {typeof fee.price === "string"
                  ? fee.price
                  : fee.price === 0
                  ? "Free"
                  : formatPrice(locale, currency, fee.price)}
              </span>
            </span>
          ))}
        </span>
      }
      className="w-96 p-4 rounded-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
    >
      <QuestionMarkCircleIcon className="w-5 h-5" />
    </Tooltip>
  ) : (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <QuestionMarkCircleIcon className="w-5 h-5" />
      </DialogTrigger>
      <AnimatePresence mode="wait">
        {open && (
          <DialogContent asChild forceMount handleClose={() => setOpen(false)}>
            <Card asChild>
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                className="flex flex-col"
                transition={{ duration: 0.2 }}
                variants={{
                  hidden: {
                    y: 200,
                    opacity: 0,
                  },
                  show: {
                    y: 0,
                    opacity: 1,
                  },
                }}
              >
                <div
                  className={joinClassNames(
                    "flex flex-col gap-4",
                    isShopsite ? "px-6 pt-6 pb-4 bg-indigo-500" : ""
                  )}
                >
                  <div className="relative self-end">
                    <DialogClose asChild>
                      <DialogCloseButton shopsiteVariant="white" />
                    </DialogClose>
                  </div>
                  <DialogHeader className="!text-left">
                    <DialogTitle asChild>
                      <Heading
                        as="h3"
                        size="5"
                        weight="bold"
                        shopsiteClasses="text-white text-xl font-bold"
                      >
                        {title}
                      </Heading>
                    </DialogTitle>
                    <DialogDescription>
                      <Text
                        color="gray"
                        shopsiteClasses="leading-5 !text-indigo-100"
                      >
                        {description}
                      </Text>
                    </DialogDescription>
                  </DialogHeader>
                </div>
                <div className="px-6">
                  {fees.map((fee) => (
                    <div
                      key={fee.id}
                      className="flex w-full items-center gap-6 mt-2 justify-between"
                    >
                      <div className="min-w-0 flex-auto">
                        <Text
                          as="p"
                          weight="medium"
                          shopsiteClasses="font-medium"
                        >
                          {fee.name}
                        </Text>
                        <Text
                          as="p"
                          sharedClasses="mt-1 text-sm leading-5"
                          shopsiteClasses="text-indigo-500"
                          whitelabelClasses="text-[var(--accent-10)]"
                        >
                          {fee.description}
                        </Text>
                      </div>
                      <span
                        className={joinClassNames(
                          "font-semibold tabular-nums",
                          typeof fee.price === "string"
                            ? fee.price.includes("$")
                              ? "mr-1"
                              : ""
                            : ""
                        )}
                      >
                        {typeof fee.price === "string"
                          ? fee.price
                          : fee.price === 0
                          ? t("free")
                          : formatPrice(locale, currency, fee.price)}
                      </span>
                    </div>
                  ))}
                </div>
                <div
                  className={joinClassNames(
                    "flex-1 flex items-center justify-center px-6",
                    isShopsite ? "pb-6" : "pb-3"
                  )}
                >
                  <DialogClose asChild>
                    <Button sharedClasses="w-full">{t("done")}</Button>
                  </DialogClose>
                </div>
              </motion.div>
            </Card>
          </DialogContent>
        )}
      </AnimatePresence>
    </Dialog>
  );
};

export default Feetooltip;
